import { useState } from 'react'

import { useApolloClient, useQuery } from '@apollo/client'
import { Loader, Select, MultiSelect, TextInput } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { useMutation, useQuery as useRedwoodQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_ADMIN_COMPANY } from 'src/graphql/companies.graphql'
import {
  ADD_PACKAGE,
  REQUEST_PACKAGES,
  DELETE_PACKAGE,
  REFRESH_PACKAGES,
} from 'src/graphql/packages.graphql'
import { PackageType } from 'src/graphql/types/package'
import { PackageTypeOptions } from 'src/graphql/types/package'
import { StepType } from 'src/graphql/types/steps'
import { formatSnakeValue } from 'src/lib/formatters'

import ActionsCell from './components/ActionsCell'
import BackgroundSettingsCard from './components/BackgroundSettingsCard'
import NameCell from './components/NameCell'
import ProviderCell from './components/ProviderCell'
import StepsCell from './components/StepsCell'
import TypeCell from './components/TypeCell'

const stepOptions = Object.values(StepType).map((v) => ({
  value: v,
  label: formatSnakeValue(v),
}))

const AddPackageRow = ({ setAdding, onDone }) => {
  const [newPkg, setNewPkg] = useState({
    name: '',
    providerPackageId: '',
    stepType: [],
    packageType: PackageType.ASURINT,
  })
  const [addPackage, { loading }] = useMutation(ADD_PACKAGE, {
    onCompleted: ({ addPackage: result }) => {
      toast(`${result.name} created successfully`, 'success')
      setNewPkg({
        name: '',
        providerPackageId: '',
        stepType: [],
        packageType: PackageType.ASURINT,
      })
      onDone()
    },
    onError: (e) => toast(e?.message || 'Unable to create package', 'error'),
    refetchQueries: [REQUEST_PACKAGES],
  })

  const handleCreate = () => {
    addPackage({ variables: { input: newPkg } })
  }

  return (
    <div className="grid grid-cols-5 gap-4 p-4">
      <Select
        data={PackageTypeOptions}
        label="Type"
        size="xs"
        value={newPkg.packageType}
        onChange={(val) =>
          setNewPkg({ ...newPkg, packageType: val as PackageType })
        }
      />
      <TextInput
        label="Name"
        size="xs"
        placeholder="Package name"
        value={newPkg.name}
        onChange={(e) => setNewPkg({ ...newPkg, name: e.target.value })}
      />
      <TextInput
        label="Provider"
        size="xs"
        placeholder="Provider ID"
        value={newPkg.providerPackageId}
        onChange={(e) =>
          setNewPkg({ ...newPkg, providerPackageId: e.target.value })
        }
      />
      <MultiSelect
        data={stepOptions}
        label="Steps"
        size="xs"
        value={newPkg.stepType}
        onChange={(val) => setNewPkg({ ...newPkg, stepType: val })}
      />
      <div className="ml-auto flex items-end gap-2">
        <DNButton variant="secondary" onClick={() => setAdding(false)}>
          Cancel
        </DNButton>
        <DNButton variant="primary" loading={loading} onClick={handleCreate}>
          Confirm
        </DNButton>
      </div>
    </div>
  )
}

const BackgroundCheckTab = () => {
  const { currentUser } = useAuth()
  const companyId = currentUser?.companyId

  const { data, loading } = useQuery(REQUEST_PACKAGES, {
    fetchPolicy: 'cache-and-network',
  })

  const { data: companyData } = useRedwoodQuery(GET_ADMIN_COMPANY, {
    variables: { id: companyId },
    skip: !companyId,
  })

  const company = companyData?.adminCompany

  const packages = data?.packages || []
  const totalCount = packages.length
  const [adding, setAdding] = useState(false)
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null
  )
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false)
  const [deletePackage] = useMutation(DELETE_PACKAGE, {
    onCompleted: ({ deletePackage: result }) => {
      toast(`${result.name} deleted successfully`, 'success')
    },
    onError: (e) => toast(e?.message || 'Unable to delete package', 'error'),
    refetchQueries: [REQUEST_PACKAGES],
  })
  const client = useApolloClient()
  const [refreshing, setRefreshing] = useState(false)

  const handleRefreshAllPackages = async () => {
    setRefreshing(true)

    try {
      await client.mutate({ mutation: REFRESH_PACKAGES })
      toast('Packages refreshed successfully', 'success')
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to refresh packages'
      toast(message, 'error')
    } finally {
      // Always refetch — partial results may have been saved to DB
      await client.refetchQueries({ include: [REQUEST_PACKAGES] })
      setRefreshing(false)
    }
  }

  const handleDelete = () => {
    if (selectedPackageId) {
      deletePackage({ variables: { packageId: selectedPackageId } })
      deleteModalHandlers.close()
    }
  }

  // Column definitions for DnTable
  const columns = [
    {
      key: 'packageType',
      label: 'Type',
      width: 15, // 15%
      render: (pkg) => <TypeCell key={`type-${pkg.packageId}`} pkg={pkg} />,
    },
    {
      key: 'name',
      label: 'Name',
      width: 30, // 30%
      render: (pkg) => <NameCell key={`name-${pkg.packageId}`} pkg={pkg} />,
    },
    {
      key: 'providerPackageId',
      label: 'Provider',
      width: 10, // 10%
      render: (pkg) => (
        <ProviderCell key={`provider-${pkg.packageId}`} pkg={pkg} />
      ),
    },
    {
      key: 'stepType',
      label: 'Steps',
      width: 35, // 35%
      render: (pkg) => <StepsCell key={`steps-${pkg.packageId}`} pkg={pkg} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 10, // 10%
      render: (pkg) => (
        <ActionsCell
          key={`actions-${pkg.packageId}`}
          pkg={pkg}
          setSelectedPackageId={setSelectedPackageId}
          deleteModalHandlers={deleteModalHandlers}
        />
      ),
    },
  ]

  return (
    <div className="flex h-full flex-col gap-6">
      {company && (
        <BackgroundSettingsCard
          companyData={{
            companyId: company.companyId,
            companyHireRightName: company.companyHireRightName,
            hireRightCompanyLogin: company.hireRightCompanyLogin,
            hireRightSubAccountCompanyLogin:
              company.hireRightSubAccountCompanyLogin,
            asurintUsername: company.asurintUsername,
            asurintPassword: company.asurintPassword,
            asurintAccountId: company.asurintAccountId,
            asurintLocationId: company.asurintLocationId,
            driverIQAccountCode: company.driverIQAccountCode,
            driverIQTenant: company.driverIQTenant,
            driverIQClientId: company.driverIQClientId,
            driverIQClientSecret: company.driverIQClientSecret,
          }}
        />
      )}
      <div className="flex h-full flex-col gap-4">
        <div className="flex-shrink-0">
          <div className="flex justify-between">
            <div className="flex flex-col">
              <div className="text-lg font-semibold text-doubleNickel-gray-900">
                Background Check
              </div>
              <div className="text-sm text-doubleNickel-gray-600">
                {loading ? 'loading...' : `${totalCount} packages`}
              </div>
            </div>
            <div className="flex gap-2">
              <DNButton
                variant="secondary"
                onClick={handleRefreshAllPackages}
                disabled={refreshing}
              >
                {refreshing && <Loader size={14} />}
                Refresh Packages
              </DNButton>
              <DNButton
                variant="primary"
                onClick={() => setAdding(true)}
                disabled={adding}
              >
                Add Package
              </DNButton>
            </div>
          </div>

          {adding && (
            <div className="mt-4 rounded border bg-gray-50">
              <AddPackageRow
                setAdding={setAdding}
                onDone={() => {
                  setAdding(false)
                }}
              />
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1">
          <DnTable
            columns={columns}
            data={packages}
            enableColumnResize={true}
            loading={loading}
          />
        </div>

        <DeleteModal
          title="Are you sure you want to delete this package?"
          opened={deleteModalOpened}
          close={deleteModalHandlers.close}
          onDelete={handleDelete}
        />
      </div>
    </div>
  )
}

export default BackgroundCheckTab
